import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from './firebase';

/**
 * Fetch all artwork created by a specific user across all their circles
 * @param {string} userId - The user's Firebase auth UID
 * @returns {Promise<Array>} Array of artwork objects with metadata
 */
export async function getUserArtworkAcrossCircles(userId) {
  if (!userId) {
    throw new Error('User ID is required');
  }

  try {
    // Step 1: Get all circles where user is a member
    const circlesQuery = query(
      collection(db, 'artPingPongCircles'),
      where('memberIds', 'array-contains', userId)
    );
    
    const circlesSnapshot = await getDocs(circlesQuery);
    const allArtwork = [];

    // Step 2: For each circle, extract user's artwork from messages
    circlesSnapshot.forEach((circleDoc) => {
      const circleData = circleDoc.data();
      const circleId = circleDoc.id;
      const circleName = circleData.name || 'Unnamed Circle';
      const messages = circleData.messages || [];

      // Filter messages that:
      // - Have an image URL
      // - Were created by this user
      // - Are marked as new artwork (not just reposts)
      const userArtworkMessages = messages.filter(msg => 
        msg.imageUrl && 
        msg.userId === userId &&
        msg.isNewArtwork === true
      );

      // Map to artwork objects with metadata
      userArtworkMessages.forEach(msg => {
        allArtwork.push({
          id: msg.id,
          imageUrl: msg.imageUrl,
          fullResImageUrl: msg.fullResImageUrl || msg.imageUrl,
          circleId: circleId,
          circleName: circleName,
          timestamp: msg.timestamp || 0,
          text: msg.text || '',
          displayName: msg.displayName || 'You',
        });
      });
    });

    // Step 3: Sort by timestamp (newest first)
    allArtwork.sort((a, b) => b.timestamp - a.timestamp);

    return allArtwork;
  } catch (error) {
    console.error('Error fetching user artwork:', error);
    throw error;
  }
}

/**
 * Get a count of how many circles the user belongs to
 * @param {string} userId - The user's Firebase auth UID
 * @returns {Promise<number>} Count of circles
 */
export async function getUserCircleCount(userId) {
  if (!userId) return 0;

  try {
    const circlesQuery = query(
      collection(db, 'artPingPongCircles'),
      where('memberIds', 'array-contains', userId)
    );
    
    const snapshot = await getDocs(circlesQuery);
    return snapshot.size;
  } catch (error) {
    console.error('Error counting user circles:', error);
    return 0;
  }
}
